/**
 * Physio Essential — Condition-specific quiz configuration.
 *
 * ARCHITECTURE
 *   • QUIZ_SYSTEMS: high-level clinical systems used on the entry screen
 *   • QUIZ_CONDITIONS[slug]: metadata + questions + product mapping + result content
 *
 * Each condition has:
 *   • questions:  ordered [{ id, q, type:'single'|'multi', opts:[{v,l, redFlag?, boost?}] }]
 *                 - `opts[].boost` = { productSlug: +weight } — per-answer modifier
 *                 - `opts[].redFlag` = true → triggers the safety gate
 *   • products:   [{ slug, weight, role:'primary'|'supporting' }] — base relevance weights
 *   • result:     headline, focus, protocol_link, protocol_label
 *
 * SAFETY: quiz is nutritional-support finder — NOT a diagnostic tool.
 * If any redFlag is selected, the engine short-circuits to a "consult professional" screen.
 * If the highest weighted product scores below RELEVANCE_THRESHOLD → no forced rec.
 */

export const RELEVANCE_THRESHOLD = 40;

// Universal red-flag options — reusable across ALL condition questionnaires.
// If ANY of these are selected the engine short-circuits to an urgent-assessment screen.
export const RED_FLAGS = {
  severe_pain: { v: "severe_pain", l: "Severe, unexplained or worsening pain", redFlag: true },
  cannot_bear_weight: { v: "cannot_bear_weight", l: "I cannot bear weight on the joint", redFlag: true },
  major_trauma: { v: "major_trauma", l: "Recent major trauma or fall", redFlag: true },
  new_swelling: { v: "new_swelling", l: "Significant new swelling or redness", redFlag: true },
  neuro_symptoms: { v: "neuro_symptoms", l: "Numbness, weakness or loss of bladder/bowel control", redFlag: true },
  chest_pain: { v: "chest_pain", l: "Chest pain or severe shortness of breath", redFlag: true },
  saddle_numbness: { v: "saddle_numbness", l: "Numbness around the groin / saddle area", redFlag: true },
  bladder_bowel: { v: "bladder_bowel", l: "New loss of bladder or bowel control", redFlag: true },
  leg_weakness: { v: "leg_weakness", l: "Rapidly worsening leg weakness", redFlag: true },
  both_legs: { v: "both_legs", l: "Severe symptoms affecting both legs", redFlag: true },
  cold_pale_foot: { v: "cold_pale_foot", l: "Cold, pale or blue-tinged foot", redFlag: true },
  black_tissue: { v: "black_tissue", l: "Black or blue-black tissue anywhere on foot / leg", redFlag: true },
  worsening_wound: { v: "worsening_wound", l: "Rapidly worsening foot ulcer / wound", redFlag: true },
  fever_wound: { v: "fever_wound", l: "Fever together with a wound or ulcer", redFlag: true },
  sudden_swelling: { v: "sudden_swelling", l: "Sudden severe leg swelling", redFlag: true },
  coughing_blood: { v: "coughing_blood", l: "Coughing up blood", redFlag: true },
  progressive_weakness: { v: "progressive_weakness", l: "Rapidly progressive weakness or numbness", redFlag: true },
};

export const QUIZ_SYSTEMS = [
  {
    key: "joint",
    name: "Joint & Orthopaedic",
    tint: "#2F80ED",
    conditions: [
      "knee-osteoarthritis",
      "hip-osteoarthritis",
      "joint-stiffness",
      "shoulder-rehabilitation",
      "joint-replacement-recovery",
    ],
  },
  {
    key: "spine",
    name: "Spine & Back",
    tint: "#0EA5E9",
    conditions: [
      "back-neck-rehabilitation",
      "disc-degeneration",
      "sciatica",
      "neck-pain",
    ],
  },
  {
    key: "sports",
    name: "Sports & Rehabilitation",
    tint: "#34D8A0",
    conditions: ["acl-rehabilitation", "sports-recovery"],
  },
  {
    key: "muscle",
    name: "Muscle",
    tint: "#14B8A6",
    conditions: ["muscle-cramps"],
  },
  {
    key: "bone",
    name: "Bone",
    tint: "#F59E0B",
    conditions: ["bone-health", "fracture-recovery"],
  },
  {
    key: "nerve",
    name: "Nerve",
    tint: "#8B5CF6",
    conditions: ["b12-support", "neuropathy"],
  },
  {
    key: "vascular",
    name: "Vascular & Venous",
    tint: "#DB2777",
    conditions: [
      "spider-veins",
      "varicose-veins",
      "chronic-venous-insufficiency",
      "leg-heaviness",
      "leg-itching",
      "calf-pain-walking",
      "smoking-vascular",
      "foot-ulcer",
    ],
  },
  {
    key: "avn",
    name: "Avascular Necrosis",
    tint: "#DC2626",
    conditions: ["hip-avn", "knee-avn", "shoulder-avn"],
  },
  {
    key: "aging",
    name: "Healthy Aging",
    tint: "#EF4444",
    conditions: ["geriatric-mobility"],
  },
];

// Product-slug constants (kept in one place so we never mistype)
const PHYSIOMOVEFLEX = "physiomoveflex-joint-mobility-collagen";
const MAG_FORTE = "mag-forte-d3-magnesium-vitamin-d3";
const MYO_RELEASE = "myo-release-muscle-cramp-tension-formula";
const B12 = "vitamin-b12-spray-1500-mcg";
const GD_NANO_D3 = "gd-nano-d3-vitamin-d3-60000-iu";
const PHYSIOFUEL = "physiofuel-am-pm-3-phase-recovery-system";
const VARIFLUX = "variflux-healthy-veins-vessels";

export const QUIZ_CONDITIONS = {
  "knee-osteoarthritis": {
    slug: "knee-osteoarthritis",
    name: "Knee Osteoarthritis",
    system: "joint",
    tag: "Knee Recovery",
    tagline: "Joint mobility & comfort nutrition.",
    result_headline: "Your Joint Recovery Pathway",
    result_focus: "Mobility + Joint Support",
    result_category: "Joint & Mobility",
    protocol_link: "/conditions/knee-osteoarthritis",
    protocol_label: "Explore Knee Recovery Protocol",
    questions: [
      {
        id: "concern",
        q: "What is your main knee concern?",
        type: "single",
        opts: [
          { v: "pain_activity", l: "Knee pain during activity", boost: { [PHYSIOMOVEFLEX]: 15 } },
          { v: "stiffness", l: "Knee stiffness", boost: { [PHYSIOMOVEFLEX]: 15 } },
          { v: "walking", l: "Difficulty walking", boost: { [PHYSIOMOVEFLEX]: 10 } },
          { v: "stairs", l: "Difficulty climbing stairs", boost: { [PHYSIOMOVEFLEX]: 10 } },
          { v: "general", l: "General joint support", boost: { [PHYSIOMOVEFLEX]: 8 } },
          RED_FLAGS.severe_pain,
        ],
      },
      {
        id: "limitation",
        q: "How does your knee affect daily activity?",
        type: "single",
        opts: [
          { v: "minimal", l: "Minimal limitation" },
          { v: "some", l: "Some limitation" },
          { v: "significant", l: "Significant limitation" },
          { v: "avoiding", l: "I'm avoiding several activities" },
        ],
      },
      {
        id: "activities",
        q: "Which activities are most difficult? (choose all that apply)",
        type: "multi",
        opts: [
          { v: "walking", l: "Walking" },
          { v: "stairs", l: "Stairs" },
          { v: "squatting", l: "Squatting" },
          { v: "standing", l: "Standing for long periods" },
          { v: "exercise", l: "Exercise" },
          { v: "sitting", l: "Getting up from a chair" },
        ],
      },
      {
        id: "physio",
        q: "Are you currently doing physiotherapy?",
        type: "single",
        opts: [
          { v: "yes", l: "Yes, I'm in an active programme", boost: { [PHYSIOMOVEFLEX]: 10, [MAG_FORTE]: 5 } },
          { v: "no", l: "No" },
          { v: "planning", l: "Planning to start" },
        ],
      },
      {
        id: "goal",
        q: "What is your primary recovery goal?",
        type: "single",
        opts: [
          { v: "mobility", l: "Improve mobility", boost: { [PHYSIOMOVEFLEX]: 12 } },
          { v: "active", l: "Stay active", boost: { [PHYSIOMOVEFLEX]: 10 } },
          { v: "joint_health", l: "Support joint health", boost: { [PHYSIOMOVEFLEX]: 12 } },
          { v: "rehab", l: "Support rehabilitation", boost: { [PHYSIOMOVEFLEX]: 10, [MAG_FORTE]: 6 } },
          { v: "independence", l: "Maintain independence" },
        ],
      },
      {
        id: "age",
        q: "What is your age group?",
        type: "single",
        opts: [
          { v: "u30", l: "Under 30" },
          { v: "30_45", l: "30–45" },
          { v: "46_60", l: "46–60", boost: { [MAG_FORTE]: 6 } },
          { v: "61_75", l: "61–75", boost: { [MAG_FORTE]: 8, [GD_NANO_D3]: 5 } },
          { v: "75_plus", l: "75+", boost: { [MAG_FORTE]: 8, [GD_NANO_D3]: 6 } },
        ],
      },
    ],
    products: [
      { slug: PHYSIOMOVEFLEX, weight: 55, role: "primary" },
      { slug: MAG_FORTE, weight: 30, role: "supporting" },
    ],
  },

  "hip-osteoarthritis": {
    slug: "hip-osteoarthritis",
    name: "Hip Osteoarthritis",
    system: "joint",
    tag: "Hip Recovery",
    tagline: "Hip mobility & everyday walking support.",
    result_headline: "Your Hip Recovery Pathway",
    result_focus: "Mobility + Hip Support",
    result_category: "Joint & Mobility",
    protocol_link: "/conditions/hip-osteoarthritis",
    protocol_label: "Explore Hip Recovery Protocol",
    questions: [
      {
        id: "concern",
        q: "What is your main hip concern?",
        type: "single",
        opts: [
          { v: "pain_walking", l: "Hip pain when walking", boost: { [PHYSIOMOVEFLEX]: 15 } },
          { v: "stiffness_morning", l: "Morning stiffness", boost: { [PHYSIOMOVEFLEX]: 12 } },
          { v: "reduced_range", l: "Reduced range of motion", boost: { [PHYSIOMOVEFLEX]: 12 } },
          { v: "general", l: "General hip support", boost: { [PHYSIOMOVEFLEX]: 8 } },
          RED_FLAGS.severe_pain,
          RED_FLAGS.cannot_bear_weight,
        ],
      },
      {
        id: "walking",
        q: "How is your walking distance today?",
        type: "single",
        opts: [
          { v: "unlimited", l: "Unlimited" },
          { v: "moderate", l: "Moderate — 15–30 minutes" },
          { v: "short", l: "Short distances only" },
          { v: "assistance", l: "I need a stick or support" },
        ],
      },
      {
        id: "activities",
        q: "Which movements are most affected? (choose all that apply)",
        type: "multi",
        opts: [
          { v: "walking", l: "Walking" },
          { v: "stairs", l: "Stairs" },
          { v: "sit_to_stand", l: "Sit-to-stand" },
          { v: "putting_shoes", l: "Putting on shoes / socks" },
          { v: "sleeping", l: "Sleeping on the affected side" },
        ],
      },
      {
        id: "physio",
        q: "Are you currently doing physiotherapy?",
        type: "single",
        opts: [
          { v: "yes", l: "Yes", boost: { [PHYSIOMOVEFLEX]: 10, [MAG_FORTE]: 5 } },
          { v: "no", l: "No" },
          { v: "planning", l: "Planning to start" },
        ],
      },
      {
        id: "goal",
        q: "What is your primary recovery goal?",
        type: "single",
        opts: [
          { v: "walk_further", l: "Walk further and more comfortably", boost: { [PHYSIOMOVEFLEX]: 12 } },
          { v: "mobility", l: "Improve mobility", boost: { [PHYSIOMOVEFLEX]: 12 } },
          { v: "joint_health", l: "Support joint health long-term", boost: { [PHYSIOMOVEFLEX]: 10 } },
          { v: "independence", l: "Maintain independence" },
        ],
      },
    ],
    products: [
      { slug: PHYSIOMOVEFLEX, weight: 55, role: "primary" },
      { slug: MAG_FORTE, weight: 25, role: "supporting" },
    ],
  },

  "joint-stiffness": {
    slug: "joint-stiffness",
    name: "Joint Stiffness & Mobility",
    system: "joint",
    tag: "Mobility",
    tagline: "General joint & mobility nutrition.",
    result_headline: "Your Mobility Pathway",
    result_focus: "Mobility + Joint Comfort",
    result_category: "Joint & Mobility",
    protocol_link: "/conditions/joint-stiffness",
    protocol_label: "Explore Mobility Protocol",
    questions: [
      {
        id: "affected_area",
        q: "Where do you feel most stiffness?",
        type: "single",
        opts: [
          { v: "knees", l: "Knees" },
          { v: "hips", l: "Hips" },
          { v: "shoulders", l: "Shoulders" },
          { v: "back", l: "Back / neck" },
          { v: "hands", l: "Hands" },
          { v: "multiple", l: "Multiple joints", boost: { [PHYSIOMOVEFLEX]: 10 } },
        ],
      },
      {
        id: "when",
        q: "When is stiffness worst?",
        type: "single",
        opts: [
          { v: "morning", l: "Morning" },
          { v: "after_rest", l: "After sitting for a while" },
          { v: "after_exercise", l: "After exercise" },
          { v: "throughout", l: "Throughout the day" },
        ],
      },
      {
        id: "activity_level",
        q: "How would you describe your activity level?",
        type: "single",
        opts: [
          { v: "very_active", l: "Very active / athlete" },
          { v: "regular", l: "Regular exercise", boost: { [PHYSIOFUEL]: 5 } },
          { v: "light", l: "Light activity" },
          { v: "sedentary", l: "Mostly sedentary" },
        ],
      },
      {
        id: "goal",
        q: "What is your main goal?",
        type: "single",
        opts: [
          { v: "mobility", l: "Better everyday mobility", boost: { [PHYSIOMOVEFLEX]: 12 } },
          { v: "activity", l: "Return to activities I enjoy", boost: { [PHYSIOMOVEFLEX]: 10 } },
          { v: "long_term", l: "Long-term joint nutrition", boost: { [PHYSIOMOVEFLEX]: 10 } },
        ],
      },
      {
        id: "physio",
        q: "Are you currently doing physiotherapy?",
        type: "single",
        opts: [
          { v: "yes", l: "Yes" },
          { v: "no", l: "No" },
          { v: "planning", l: "Planning to start" },
        ],
      },
    ],
    products: [
      { slug: PHYSIOMOVEFLEX, weight: 50, role: "primary" },
      { slug: MAG_FORTE, weight: 25, role: "supporting" },
    ],
  },

  "shoulder-rehabilitation": {
    slug: "shoulder-rehabilitation",
    name: "Shoulder Rehabilitation",
    system: "joint",
    tag: "Shoulder Recovery",
    tagline: "Range-of-motion & strength nutrition.",
    result_headline: "Your Shoulder Recovery Pathway",
    result_focus: "Range of Motion + Strength",
    result_category: "Joint & Mobility",
    protocol_link: "/conditions/shoulder-rehabilitation",
    protocol_label: "Explore Shoulder Recovery Protocol",
    questions: [
      {
        id: "cause",
        q: "What is the main issue you're recovering from?",
        type: "single",
        opts: [
          { v: "general_stiffness", l: "General shoulder stiffness" },
          { v: "post_surgery", l: "Post-surgery rehabilitation", boost: { [PHYSIOMOVEFLEX]: 12 } },
          { v: "sports_injury", l: "Sports-related injury", boost: { [PHYSIOFUEL]: 8 } },
          { v: "strength_loss", l: "Loss of strength", boost: { [PHYSIOMOVEFLEX]: 8 } },
          RED_FLAGS.severe_pain,
        ],
      },
      {
        id: "movement",
        q: "Which movement is most difficult?",
        type: "single",
        opts: [
          { v: "overhead", l: "Reaching overhead" },
          { v: "behind_back", l: "Reaching behind the back" },
          { v: "lifting", l: "Lifting" },
          { v: "exercise", l: "Exercise" },
        ],
      },
      {
        id: "physio",
        q: "Are you currently doing physiotherapy?",
        type: "single",
        opts: [
          { v: "yes", l: "Yes", boost: { [PHYSIOMOVEFLEX]: 10 } },
          { v: "no", l: "No" },
          { v: "planning", l: "Planning to start" },
        ],
      },
      {
        id: "goal",
        q: "What is your main goal?",
        type: "single",
        opts: [
          { v: "mobility", l: "Restore mobility", boost: { [PHYSIOMOVEFLEX]: 12 } },
          { v: "strength", l: "Rebuild strength", boost: { [PHYSIOFUEL]: 8 } },
          { v: "return", l: "Return to activity / sport", boost: { [PHYSIOFUEL]: 10 } },
          { v: "recovery", l: "Recovery support", boost: { [PHYSIOMOVEFLEX]: 8 } },
        ],
      },
    ],
    products: [
      { slug: PHYSIOMOVEFLEX, weight: 50, role: "primary" },
      { slug: MAG_FORTE, weight: 20, role: "supporting" },
    ],
  },

  "back-neck-rehabilitation": {
    slug: "back-neck-rehabilitation",
    name: "Back & Neck Rehabilitation",
    system: "joint",
    tag: "Spine Recovery",
    tagline: "Nutrition alongside spine-focused physio.",
    result_headline: "Your Spine Recovery Pathway",
    result_focus: "Mobility + Everyday Comfort",
    result_category: "Joint & Mobility",
    protocol_link: "/conditions/back-neck-rehabilitation",
    protocol_label: "Explore Back & Neck Protocol",
    questions: [
      {
        id: "area",
        q: "Which area are you focused on?",
        type: "single",
        opts: [
          { v: "lower_back", l: "Lower back" },
          { v: "mid_back", l: "Mid back" },
          { v: "neck", l: "Neck" },
          { v: "multiple", l: "Multiple areas" },
        ],
      },
      {
        id: "trigger",
        q: "When are you most uncomfortable? (choose all that apply)",
        type: "multi",
        opts: [
          { v: "sitting", l: "Sitting for long periods" },
          { v: "standing", l: "Standing for long periods" },
          { v: "lifting", l: "Lifting" },
          { v: "sleeping", l: "Sleeping / on waking" },
          { v: "exercise", l: "Exercise" },
          RED_FLAGS.neuro_symptoms,
        ],
      },
      {
        id: "physio",
        q: "Are you currently doing physiotherapy?",
        type: "single",
        opts: [
          { v: "yes", l: "Yes", boost: { [PHYSIOMOVEFLEX]: 10 } },
          { v: "no", l: "No" },
          { v: "planning", l: "Planning to start" },
        ],
      },
      {
        id: "goal",
        q: "What is your main goal?",
        type: "single",
        opts: [
          { v: "mobility", l: "Better everyday mobility", boost: { [PHYSIOMOVEFLEX]: 10 } },
          { v: "posture", l: "Support posture and strengthening", boost: { [PHYSIOMOVEFLEX]: 8, [MAG_FORTE]: 5 } },
          { v: "recovery", l: "Everyday recovery support", boost: { [MAG_FORTE]: 8 } },
        ],
      },
    ],
    products: [
      { slug: PHYSIOMOVEFLEX, weight: 45, role: "primary" },
      { slug: MAG_FORTE, weight: 25, role: "supporting" },
    ],
  },

  "joint-replacement-recovery": {
    slug: "joint-replacement-recovery",
    name: "Joint Replacement Recovery",
    system: "joint",
    tag: "Post-Op Rehab",
    tagline: "Nutrition alongside your post-op rehabilitation plan.",
    result_headline: "Your Post-Op Recovery Pathway",
    result_focus: "Rehab-stage Nutrition",
    result_category: "Joint & Mobility",
    protocol_link: "/conditions/joint-replacement-recovery",
    protocol_label: "Explore Post-Op Recovery Protocol",
    questions: [
      {
        id: "when",
        q: "How long ago was your surgery?",
        type: "single",
        opts: [
          { v: "planned", l: "Surgery is planned" },
          { v: "under_2", l: "Less than 2 weeks", boost: { [PHYSIOMOVEFLEX]: 8, [MAG_FORTE]: 8 } },
          { v: "2_6", l: "2–6 weeks", boost: { [PHYSIOMOVEFLEX]: 12, [MAG_FORTE]: 6 } },
          { v: "6_12", l: "6–12 weeks", boost: { [PHYSIOMOVEFLEX]: 14, [PHYSIOFUEL]: 5 } },
          { v: "3_6m", l: "3–6 months", boost: { [PHYSIOMOVEFLEX]: 12, [PHYSIOFUEL]: 8 } },
          { v: "over_6m", l: "More than 6 months", boost: { [PHYSIOMOVEFLEX]: 10 } },
        ],
      },
      {
        id: "physio",
        q: "Are you currently doing physiotherapy?",
        type: "single",
        opts: [
          { v: "yes", l: "Yes", boost: { [PHYSIOMOVEFLEX]: 10 } },
          { v: "no", l: "No" },
          { v: "planning", l: "Planning to start" },
        ],
      },
      {
        id: "challenge",
        q: "What is currently your biggest challenge?",
        type: "single",
        opts: [
          { v: "stiffness", l: "Joint stiffness" },
          { v: "walking", l: "Walking" },
          { v: "strength", l: "Strength", boost: { [PHYSIOFUEL]: 6 } },
          { v: "balance", l: "Balance" },
          { v: "return_daily", l: "Returning to daily activities" },
          RED_FLAGS.new_swelling,
        ],
      },
      {
        id: "mobility",
        q: "What is your current mobility level?",
        type: "single",
        opts: [
          { v: "resting", l: "Mostly resting" },
          { v: "assisted", l: "Walking with assistance" },
          { v: "independent", l: "Walking independently" },
          { v: "long_distance", l: "Walking longer distances" },
          { v: "normal", l: "Returning to normal activities" },
        ],
      },
      {
        id: "goal",
        q: "What is your recovery goal?",
        type: "single",
        opts: [
          { v: "mobility", l: "Improve mobility", boost: { [PHYSIOMOVEFLEX]: 10 } },
          { v: "strength", l: "Improve strength", boost: { [PHYSIOFUEL]: 8 } },
          { v: "return", l: "Return to daily activities", boost: { [PHYSIOMOVEFLEX]: 8 } },
          { v: "long_term", l: "Long-term joint function", boost: { [PHYSIOMOVEFLEX]: 10 } },
        ],
      },
    ],
    products: [
      { slug: PHYSIOMOVEFLEX, weight: 45, role: "primary" },
      { slug: MAG_FORTE, weight: 20, role: "supporting" },
      { slug: PHYSIOFUEL, weight: 15, role: "supporting" },
    ],
  },

  "acl-rehabilitation": {
    slug: "acl-rehabilitation",
    name: "ACL Rehabilitation",
    system: "sports",
    tag: "ACL Recovery",
    tagline: "Recovery nutrition for the ACL journey.",
    result_headline: "Your ACL Recovery Pathway",
    result_focus: "Strength + Active Recovery",
    result_category: "Sports & Recovery Nutrition",
    protocol_link: "/conditions/acl-rehabilitation",
    protocol_label: "Explore ACL Rehabilitation Protocol",
    questions: [
      {
        id: "stage",
        q: "Where are you in your ACL journey?",
        type: "single",
        opts: [
          { v: "recent_injury", l: "Recently injured", boost: { [PHYSIOMOVEFLEX]: 8 } },
          { v: "pre_surgery", l: "Pre-surgery" },
          { v: "post_surgery", l: "Recently had surgery", boost: { [PHYSIOMOVEFLEX]: 12, [MAG_FORTE]: 6 } },
          { v: "early_rehab", l: "Early rehabilitation", boost: { [PHYSIOMOVEFLEX]: 12, [MYO_RELEASE]: 6 } },
          { v: "strengthening", l: "Strengthening phase", boost: { [PHYSIOFUEL]: 14, [MYO_RELEASE]: 8 } },
          { v: "return_sport", l: "Return-to-sport phase", boost: { [PHYSIOFUEL]: 16, [MYO_RELEASE]: 8 } },
        ],
      },
      {
        id: "physio",
        q: "Are you currently under physiotherapy supervision?",
        type: "single",
        opts: [
          { v: "yes", l: "Yes", boost: { [PHYSIOMOVEFLEX]: 8 } },
          { v: "no", l: "No" },
          { v: "planning", l: "Planning to start" },
        ],
      },
      {
        id: "priority",
        q: "What is your current priority?",
        type: "single",
        opts: [
          { v: "mobility", l: "Mobility", boost: { [PHYSIOMOVEFLEX]: 10 } },
          { v: "muscle_activation", l: "Muscle activation", boost: { [MYO_RELEASE]: 8 } },
          { v: "strength", l: "Strength", boost: { [PHYSIOFUEL]: 12 } },
          { v: "recovery", l: "Recovery", boost: { [PHYSIOFUEL]: 10, [MYO_RELEASE]: 6 } },
          { v: "return", l: "Return to sport", boost: { [PHYSIOFUEL]: 14 } },
        ],
      },
      {
        id: "sport",
        q: "What activity are you returning to?",
        type: "single",
        opts: [
          { v: "gym", l: "Gym" },
          { v: "running", l: "Running", boost: { [PHYSIOFUEL]: 6 } },
          { v: "football", l: "Football", boost: { [PHYSIOFUEL]: 8 } },
          { v: "cricket", l: "Cricket", boost: { [PHYSIOFUEL]: 6 } },
          { v: "basketball", l: "Basketball", boost: { [PHYSIOFUEL]: 6 } },
          { v: "other", l: "Other sport" },
          { v: "general", l: "General activity" },
        ],
      },
      {
        id: "training",
        q: "What is your current training level?",
        type: "single",
        opts: [
          { v: "none", l: "Not training" },
          { v: "light", l: "Light rehabilitation" },
          { v: "moderate", l: "Moderate training", boost: { [PHYSIOFUEL]: 8 } },
          { v: "full", l: "Full training", boost: { [PHYSIOFUEL]: 12, [MYO_RELEASE]: 6 } },
        ],
      },
      {
        id: "goal",
        q: "What is your main nutritional goal?",
        type: "single",
        opts: [
          { v: "general", l: "General recovery", boost: { [PHYSIOFUEL]: 8 } },
          { v: "muscle", l: "Muscle recovery", boost: { [MYO_RELEASE]: 10 } },
          { v: "active", l: "Active recovery", boost: { [PHYSIOFUEL]: 10 } },
          { v: "joint", l: "Bone/joint nutritional support", boost: { [PHYSIOMOVEFLEX]: 10 } },
        ],
      },
    ],
    products: [
      { slug: PHYSIOFUEL, weight: 40, role: "primary" },
      { slug: MYO_RELEASE, weight: 25, role: "supporting" },
      { slug: PHYSIOMOVEFLEX, weight: 20, role: "supporting" },
    ],
  },

  "sports-recovery": {
    slug: "sports-recovery",
    name: "Sports Recovery",
    system: "sports",
    tag: "Training Recovery",
    tagline: "Daily recovery nutrition for active lifestyles.",
    result_headline: "Your Training Recovery Pathway",
    result_focus: "Recovery + Performance",
    result_category: "Sports & Recovery Nutrition",
    protocol_link: "/conditions/sports-recovery",
    protocol_label: "Explore Sports Recovery Protocol",
    questions: [
      {
        id: "sport",
        q: "What sport or activity do you do?",
        type: "single",
        opts: [
          { v: "running", l: "Running", boost: { [PHYSIOFUEL]: 10 } },
          { v: "football", l: "Football", boost: { [PHYSIOFUEL]: 10, [MYO_RELEASE]: 4 } },
          { v: "cricket", l: "Cricket", boost: { [PHYSIOFUEL]: 8 } },
          { v: "basketball", l: "Basketball", boost: { [PHYSIOFUEL]: 8 } },
          { v: "cycling", l: "Cycling", boost: { [PHYSIOFUEL]: 8 } },
          { v: "gym", l: "Gym", boost: { [PHYSIOFUEL]: 8, [MYO_RELEASE]: 6 } },
          { v: "other", l: "Other" },
        ],
      },
      {
        id: "frequency",
        q: "How often do you train?",
        type: "single",
        opts: [
          { v: "1_2", l: "1–2 days / week" },
          { v: "3_4", l: "3–4 days / week", boost: { [PHYSIOFUEL]: 6 } },
          { v: "5_6", l: "5–6 days / week", boost: { [PHYSIOFUEL]: 10, [MYO_RELEASE]: 4 } },
          { v: "daily", l: "Daily", boost: { [PHYSIOFUEL]: 12, [MYO_RELEASE]: 6 } },
        ],
      },
      {
        id: "concern",
        q: "What is your main recovery concern?",
        type: "single",
        opts: [
          { v: "fatigue", l: "Muscle fatigue", boost: { [MYO_RELEASE]: 8 } },
          { v: "soreness", l: "Muscle soreness", boost: { [MYO_RELEASE]: 8 } },
          { v: "joint", l: "Joint support", boost: { [PHYSIOMOVEFLEX]: 8 } },
          { v: "energy", l: "Energy / recovery", boost: { [PHYSIOFUEL]: 10 } },
          { v: "consistency", l: "Training consistency", boost: { [PHYSIOFUEL]: 6 } },
        ],
      },
      {
        id: "goal",
        q: "What is your training goal?",
        type: "single",
        opts: [
          { v: "fitness", l: "General fitness" },
          { v: "strength", l: "Strength", boost: { [PHYSIOFUEL]: 6 } },
          { v: "endurance", l: "Endurance", boost: { [PHYSIOFUEL]: 8 } },
          { v: "performance", l: "Performance", boost: { [PHYSIOFUEL]: 10, [MYO_RELEASE]: 4 } },
          { v: "return", l: "Return from injury" },
        ],
      },
      {
        id: "injury",
        q: "Are you currently recovering from an injury?",
        type: "single",
        opts: [
          { v: "no", l: "No" },
          { v: "muscle", l: "Yes — muscle", boost: { [MYO_RELEASE]: 10 } },
          { v: "joint", l: "Yes — joint", boost: { [PHYSIOMOVEFLEX]: 10 } },
        ],
      },
    ],
    products: [
      { slug: PHYSIOFUEL, weight: 40, role: "primary" },
      { slug: MYO_RELEASE, weight: 22, role: "supporting" },
    ],
  },

  "muscle-cramps": {
    slug: "muscle-cramps",
    name: "Muscle Cramps",
    system: "muscle",
    tag: "Muscle Recovery",
    tagline: "Nutrition for normal muscle function.",
    result_headline: "Your Muscle Recovery Pathway",
    result_focus: "Muscle Function + Recovery",
    result_category: "Muscle Recovery",
    protocol_link: "/conditions/muscle-cramps",
    protocol_label: "Explore Muscle Recovery Protocol",
    questions: [
      {
        id: "location",
        q: "Where do you usually experience cramps?",
        type: "single",
        opts: [
          { v: "calf", l: "Calf" },
          { v: "thigh", l: "Thigh" },
          { v: "foot", l: "Foot" },
          { v: "multiple", l: "Multiple areas", boost: { [MYO_RELEASE]: 6, [MAG_FORTE]: 4 } },
          { v: "other", l: "Other" },
        ],
      },
      {
        id: "when",
        q: "When do cramps usually occur?",
        type: "single",
        opts: [
          { v: "during", l: "During exercise", boost: { [MYO_RELEASE]: 8 } },
          { v: "after", l: "Immediately after exercise", boost: { [MYO_RELEASE]: 8 } },
          { v: "night", l: "At night", boost: { [MAG_FORTE]: 8 } },
          { v: "day", l: "During the day" },
          { v: "random", l: "Randomly" },
        ],
      },
      {
        id: "frequency",
        q: "How often?",
        type: "single",
        opts: [
          { v: "occasional", l: "Occasionally" },
          { v: "weekly", l: "Weekly", boost: { [MYO_RELEASE]: 6 } },
          { v: "several", l: "Several times a week", boost: { [MYO_RELEASE]: 10, [MAG_FORTE]: 4 } },
          { v: "daily", l: "Almost daily", boost: { [MYO_RELEASE]: 12, [MAG_FORTE]: 6 } },
        ],
      },
      {
        id: "activity",
        q: "How physically active are you?",
        type: "single",
        opts: [
          { v: "athlete", l: "Athlete", boost: { [MYO_RELEASE]: 8, [PHYSIOFUEL]: 4 } },
          { v: "gym", l: "Gym user", boost: { [MYO_RELEASE]: 6 } },
          { v: "regular", l: "Regular exercise" },
          { v: "light", l: "Light activity" },
          { v: "sedentary", l: "Mostly sedentary" },
        ],
      },
      {
        id: "goal",
        q: "What is your primary goal?",
        type: "single",
        opts: [
          { v: "muscle_function", l: "Muscle function", boost: { [MYO_RELEASE]: 10 } },
          { v: "recovery", l: "Recovery", boost: { [MYO_RELEASE]: 8 } },
          { v: "exercise", l: "Exercise support", boost: { [PHYSIOFUEL]: 8 } },
          { v: "general", l: "General nutritional support" },
        ],
      },
    ],
    products: [
      { slug: MYO_RELEASE, weight: 45, role: "primary" },
      { slug: MAG_FORTE, weight: 25, role: "supporting" },
    ],
  },

  "bone-health": {
    slug: "bone-health",
    name: "Bone Health",
    system: "bone",
    tag: "Bone Support",
    tagline: "Bone & muscle nutritional support.",
    result_headline: "Your Bone Health Pathway",
    result_focus: "Bone + Muscle Function",
    result_category: "Bone Health",
    protocol_link: "/conditions/bone-health",
    protocol_label: "Explore Bone Health Protocol",
    questions: [
      {
        id: "reason",
        q: "What is your main reason for exploring bone health?",
        type: "single",
        opts: [
          { v: "general", l: "General bone health", boost: { [GD_NANO_D3]: 8 } },
          { v: "aging", l: "Healthy aging", boost: { [GD_NANO_D3]: 8, [MAG_FORTE]: 6 } },
          { v: "vit_d", l: "Vitamin D support", boost: { [GD_NANO_D3]: 12 } },
          { v: "fracture", l: "Fracture recovery", boost: { [GD_NANO_D3]: 12, [MAG_FORTE]: 6 } },
          { v: "active", l: "Exercise / active lifestyle", boost: { [PHYSIOFUEL]: 4 } },
        ],
      },
      {
        id: "age",
        q: "Which age group are you in?",
        type: "single",
        opts: [
          { v: "u30", l: "Under 30" },
          { v: "30_45", l: "30–45" },
          { v: "46_60", l: "46–60", boost: { [GD_NANO_D3]: 4 } },
          { v: "61_75", l: "61–75", boost: { [GD_NANO_D3]: 6, [MAG_FORTE]: 4 } },
          { v: "75_plus", l: "75+", boost: { [GD_NANO_D3]: 8, [MAG_FORTE]: 6 } },
        ],
      },
      {
        id: "advised",
        q: "Have you been advised to take vitamin D?",
        type: "single",
        opts: [
          { v: "yes", l: "Yes", boost: { [GD_NANO_D3]: 12 } },
          { v: "no", l: "No" },
          { v: "unsure", l: "Not sure" },
        ],
      },
      {
        id: "activity",
        q: "Are you currently physically active?",
        type: "single",
        opts: [
          { v: "very", l: "Regularly active" },
          { v: "moderate", l: "Moderately active" },
          { v: "light", l: "Lightly active" },
          { v: "sedentary", l: "Mostly sedentary" },
        ],
      },
      {
        id: "goal",
        q: "What is your primary goal?",
        type: "single",
        opts: [
          { v: "bone", l: "Bone support", boost: { [GD_NANO_D3]: 10 } },
          { v: "muscle", l: "Muscle function", boost: { [MAG_FORTE]: 8 } },
          { v: "aging", l: "Healthy aging", boost: { [GD_NANO_D3]: 6, [MAG_FORTE]: 4 } },
          { v: "recovery", l: "Recovery support", boost: { [MAG_FORTE]: 6 } },
        ],
      },
    ],
    products: [
      { slug: GD_NANO_D3, weight: 40, role: "primary" },
      { slug: MAG_FORTE, weight: 25, role: "supporting" },
    ],
  },

  "fracture-recovery": {
    slug: "fracture-recovery",
    name: "Fracture Recovery",
    system: "bone",
    tag: "Bone Recovery",
    tagline: "Bone-health nutrition during rehabilitation.",
    result_headline: "Your Bone Recovery Pathway",
    result_focus: "Bone + Rehabilitation Nutrition",
    result_category: "Bone Health",
    protocol_link: "/conditions/fracture-recovery",
    protocol_label: "Explore Fracture Recovery Protocol",
    questions: [
      {
        id: "site",
        q: "What type of fracture are you recovering from?",
        type: "single",
        opts: [
          { v: "upper", l: "Upper limb" },
          { v: "lower", l: "Lower limb" },
          { v: "hip", l: "Hip / pelvis", boost: { [GD_NANO_D3]: 6, [MAG_FORTE]: 4 } },
          { v: "spine", l: "Spine" },
          { v: "other", l: "Other" },
        ],
      },
      {
        id: "stage",
        q: "What stage are you in?",
        type: "single",
        opts: [
          { v: "recent", l: "Recently injured", boost: { [GD_NANO_D3]: 10, [MAG_FORTE]: 6 } },
          { v: "post_surgery", l: "Post-surgery", boost: { [GD_NANO_D3]: 10, [MAG_FORTE]: 6 } },
          { v: "early", l: "Early recovery", boost: { [GD_NANO_D3]: 12 } },
          { v: "rehab", l: "Rehabilitation", boost: { [PHYSIOMOVEFLEX]: 8, [GD_NANO_D3]: 8 } },
          { v: "return", l: "Returning to activity", boost: { [PHYSIOFUEL]: 6 } },
        ],
      },
      {
        id: "physio",
        q: "Are you currently receiving physiotherapy?",
        type: "single",
        opts: [
          { v: "yes", l: "Yes", boost: { [PHYSIOMOVEFLEX]: 6 } },
          { v: "no", l: "No" },
          { v: "planning", l: "Planning to start" },
        ],
      },
      {
        id: "focus",
        q: "What is your main recovery focus?",
        type: "single",
        opts: [
          { v: "mobility", l: "Mobility", boost: { [PHYSIOMOVEFLEX]: 8 } },
          { v: "strength", l: "Strength", boost: { [PHYSIOFUEL]: 6 } },
          { v: "bone", l: "Bone health", boost: { [GD_NANO_D3]: 12 } },
          { v: "return_daily", l: "Return to daily activities" },
        ],
      },
      {
        id: "advised",
        q: "Has your healthcare professional recommended vitamin D or supplementation?",
        type: "single",
        opts: [
          { v: "yes", l: "Yes", boost: { [GD_NANO_D3]: 14 } },
          { v: "no", l: "No" },
          { v: "unsure", l: "Not sure" },
        ],
      },
    ],
    products: [
      { slug: GD_NANO_D3, weight: 40, role: "primary" },
      { slug: MAG_FORTE, weight: 22, role: "supporting" },
      { slug: PHYSIOMOVEFLEX, weight: 18, role: "supporting" },
    ],
  },

  "b12-support": {
    slug: "b12-support",
    name: "B12 / Nerve Health",
    system: "nerve",
    tag: "B12 Support",
    tagline: "Convenient vitamin B12 nutritional support.",
    result_headline: "Your B12 Nutritional Support Pathway",
    result_focus: "B12 Nutritional Support",
    result_category: "Nerve Health",
    protocol_link: "/conditions/b12-support",
    protocol_label: "Explore B12 Support Guide",
    questions: [
      {
        id: "why",
        q: "Why are you looking for B12 support?",
        type: "single",
        opts: [
          { v: "advised", l: "I have been advised to take B12", boost: { [B12]: 15 } },
          { v: "diet", l: "My diet may be low in B12", boost: { [B12]: 12 } },
          { v: "general", l: "I am looking for general nutritional support", boost: { [B12]: 8 } },
          { v: "learn", l: "I want to learn about B12" },
          RED_FLAGS.neuro_symptoms,
        ],
      },
      {
        id: "tested",
        q: "Have you had a recent B12 blood test?",
        type: "single",
        opts: [
          { v: "yes_low", l: "Yes — low result", boost: { [B12]: 12 } },
          { v: "yes_normal", l: "Yes — normal result" },
          { v: "no", l: "No" },
          { v: "unsure", l: "Not sure" },
        ],
      },
      {
        id: "recommended",
        q: "Has a healthcare professional recommended B12 supplementation?",
        type: "single",
        opts: [
          { v: "yes", l: "Yes", boost: { [B12]: 14 } },
          { v: "no", l: "No" },
          { v: "unsure", l: "Not sure" },
        ],
      },
      {
        id: "goal",
        q: "What type of support are you looking for?",
        type: "single",
        opts: [
          { v: "convenient", l: "Convenient daily B12", boost: { [B12]: 12 } },
          { v: "general", l: "General nutritional support", boost: { [B12]: 6 } },
          { v: "nerve", l: "Nerve-health nutritional support", boost: { [B12]: 10 } },
        ],
      },
    ],
    products: [
      { slug: B12, weight: 50, role: "primary" },
    ],
  },

  "geriatric-mobility": {
    slug: "geriatric-mobility",
    name: "Healthy Aging & Mobility",
    system: "aging",
    tag: "Healthy Aging",
    tagline: "Mobility, bone & muscle nutrition for later life.",
    result_headline: "Your Healthy Aging Pathway",
    result_focus: "Mobility + Bone & Muscle Support",
    result_category: "Joint & Mobility",
    protocol_link: "/conditions/geriatric-mobility",
    protocol_label: "Explore Healthy Aging Protocol",
    questions: [
      {
        id: "goal",
        q: "What is your main goal?",
        type: "single",
        opts: [
          { v: "mobility", l: "Maintain mobility", boost: { [PHYSIOMOVEFLEX]: 12 } },
          { v: "independence", l: "Stay independent", boost: { [PHYSIOMOVEFLEX]: 8 } },
          { v: "joint", l: "Support joint health", boost: { [PHYSIOMOVEFLEX]: 10 } },
          { v: "muscle", l: "Support muscle function", boost: { [MAG_FORTE]: 8, [MYO_RELEASE]: 4 } },
          { v: "bone", l: "Support bone health", boost: { [GD_NANO_D3]: 10 } },
        ],
      },
      {
        id: "difficulty",
        q: "Which activities are currently difficult? (choose all that apply)",
        type: "multi",
        opts: [
          { v: "walking", l: "Walking" },
          { v: "stairs", l: "Stairs" },
          { v: "sit_stand", l: "Getting up from a chair" },
          { v: "balance", l: "Balance" },
          { v: "exercise", l: "Exercise" },
          RED_FLAGS.cannot_bear_weight,
        ],
      },
      {
        id: "physio",
        q: "Are you currently doing physiotherapy or exercise?",
        type: "single",
        opts: [
          { v: "yes", l: "Yes", boost: { [PHYSIOMOVEFLEX]: 6 } },
          { v: "no", l: "No" },
          { v: "planning", l: "Planning to start" },
        ],
      },
      {
        id: "focus",
        q: "Which area is your main focus?",
        type: "single",
        opts: [
          { v: "joints", l: "Joints", boost: { [PHYSIOMOVEFLEX]: 10 } },
          { v: "muscles", l: "Muscles", boost: { [MAG_FORTE]: 8 } },
          { v: "bones", l: "Bones", boost: { [GD_NANO_D3]: 10 } },
          { v: "general", l: "General mobility", boost: { [PHYSIOMOVEFLEX]: 6 } },
        ],
      },
      {
        id: "support",
        q: "What type of nutritional support are you looking for?",
        type: "single",
        opts: [
          { v: "joint", l: "Joint support", boost: { [PHYSIOMOVEFLEX]: 10 } },
          { v: "bone", l: "Bone support", boost: { [GD_NANO_D3]: 10 } },
          { v: "muscle", l: "Muscle support", boost: { [MAG_FORTE]: 8 } },
          { v: "recovery", l: "General recovery nutrition", boost: { [PHYSIOFUEL]: 4 } },
        ],
      },
    ],
    products: [
      { slug: PHYSIOMOVEFLEX, weight: 40, role: "primary" },
      { slug: MAG_FORTE, weight: 22, role: "supporting" },
      { slug: GD_NANO_D3, weight: 18, role: "supporting" },
    ],
  },

  // ─── SPINE PATHWAYS ────────────────────────────────────────────────────────

  "disc-degeneration": {
    slug: "disc-degeneration",
    name: "Disc Degeneration",
    system: "spine",
    tag: "Spine Recovery",
    tagline: "Rehabilitation-first nutrition for the spine.",
    result_headline: "Your Spine Rehabilitation Pathway",
    result_focus: "Movement + Rehabilitation Support",
    result_category: "Joint & Mobility",
    protocol_link: "/conditions/back-neck-rehabilitation",
    protocol_label: "Explore Spine Rehabilitation Protocol",
    disclaimer: "Physio Essential products are nutritional supplements. They do not regenerate discs or reverse degeneration. Rehabilitation is led by your clinician and physiotherapist.",
    questions: [
      {
        id: "area",
        q: "Where is the main problem?",
        type: "single",
        opts: [
          { v: "lower", l: "Lower back" },
          { v: "neck", l: "Neck" },
          { v: "mid", l: "Mid-back" },
          { v: "multiple", l: "Multiple areas" },
        ],
      },
      {
        id: "concern",
        q: "What is your main concern?",
        type: "single",
        opts: [
          { v: "pain", l: "Back pain" },
          { v: "stiffness", l: "Stiffness" },
          { v: "mobility", l: "Reduced mobility", boost: { [PHYSIOMOVEFLEX]: 8 } },
          { v: "daily", l: "Difficulty with daily activities" },
          { v: "exercise", l: "Exercise limitation" },
          RED_FLAGS.neuro_symptoms,
        ],
      },
      {
        id: "movement",
        q: "How does movement affect your symptoms?",
        type: "single",
        opts: [
          { v: "helps", l: "Movement generally helps", boost: { [PHYSIOMOVEFLEX]: 6 } },
          { v: "worsens", l: "Movement makes symptoms worse" },
          { v: "certain", l: "Certain movements trigger symptoms" },
          { v: "constant", l: "Symptoms present regardless of movement" },
          { v: "unsure", l: "Not sure" },
        ],
      },
      {
        id: "function",
        q: "How is your daily function affected?",
        type: "single",
        opts: [
          { v: "minimal", l: "Minimal limitation" },
          { v: "some", l: "Some limitation" },
          { v: "significant", l: "Significant limitation" },
          { v: "avoiding", l: "I am avoiding normal activities" },
        ],
      },
      {
        id: "physio",
        q: "Are you currently doing physiotherapy?",
        type: "single",
        opts: [
          { v: "yes", l: "Yes", boost: { [PHYSIOMOVEFLEX]: 10 } },
          { v: "no", l: "No" },
          { v: "planning", l: "Planning to start" },
        ],
      },
      {
        id: "goal",
        q: "What is your primary recovery goal?",
        type: "single",
        opts: [
          { v: "mobility", l: "Mobility", boost: { [PHYSIOMOVEFLEX]: 10 } },
          { v: "strength", l: "Strength", boost: { [PHYSIOFUEL]: 6 } },
          { v: "return", l: "Return to activity", boost: { [PHYSIOFUEL]: 6 } },
          { v: "daily", l: "Improve daily function", boost: { [PHYSIOMOVEFLEX]: 6 } },
          { v: "general", l: "General recovery support" },
        ],
      },
      {
        id: "assessed",
        q: "Have you been assessed or imaged by a healthcare professional?",
        type: "single",
        opts: [
          { v: "yes", l: "Yes" },
          { v: "no", l: "No" },
          { v: "unsure", l: "Not sure" },
        ],
      },
    ],
    products: [
      { slug: PHYSIOMOVEFLEX, weight: 40, role: "primary" },
      { slug: MAG_FORTE, weight: 20, role: "supporting" },
    ],
  },

  "sciatica": {
    slug: "sciatica",
    name: "Sciatica / Leg-Radiating Symptoms",
    system: "spine",
    tag: "Sciatica",
    tagline: "Rehabilitation-led recovery navigation.",
    result_headline: "Your Sciatica Rehabilitation Pathway",
    result_focus: "Rehabilitation Support",
    result_category: "Joint & Mobility",
    protocol_link: "/conditions/back-neck-rehabilitation",
    protocol_label: "Explore Spine Rehabilitation Protocol",
    disclaimer: "This tool does not diagnose sciatica or nerve compression. Persistent, worsening or neurological symptoms need professional assessment.",
    questions: [
      {
        id: "safety_gate",
        q: "Before we continue — do you have ANY of the following? (choose all that apply)",
        type: "multi",
        opts: [
          RED_FLAGS.bladder_bowel,
          RED_FLAGS.saddle_numbness,
          RED_FLAGS.leg_weakness,
          RED_FLAGS.both_legs,
          RED_FLAGS.major_trauma,
          { v: "none", l: "None of these" },
        ],
      },
      {
        id: "distribution",
        q: "Where does the discomfort travel?",
        type: "single",
        opts: [
          { v: "back_only", l: "Lower back only" },
          { v: "buttock", l: "Back into buttock" },
          { v: "thigh", l: "Buttock into thigh" },
          { v: "leg", l: "Down the leg" },
          { v: "foot", l: "Into foot / toes" },
        ],
      },
      {
        id: "sensations",
        q: "Do you experience any of these? (choose all that apply)",
        type: "multi",
        opts: [
          { v: "tingling", l: "Tingling" },
          { v: "numbness", l: "Numbness" },
          { v: "burning", l: "Burning sensation" },
          { v: "electric", l: "Electric / shooting sensation" },
          { v: "none", l: "None of these" },
        ],
      },
      {
        id: "cough_trigger",
        q: "Does coughing, sneezing or certain movements affect symptoms?",
        type: "single",
        opts: [
          { v: "yes", l: "Yes" },
          { v: "no", l: "No" },
          { v: "unsure", l: "Not sure" },
        ],
      },
      {
        id: "weakness",
        q: "Is there weakness in the leg or foot?",
        type: "single",
        opts: [
          { v: "no", l: "No" },
          { v: "yes", l: "Yes", redFlag: true },
          { v: "unsure", l: "Not sure" },
        ],
      },
      {
        id: "care",
        q: "Are you currently under medical / physiotherapy care?",
        type: "single",
        opts: [
          { v: "yes", l: "Yes", boost: { [PHYSIOMOVEFLEX]: 8 } },
          { v: "no", l: "No" },
          { v: "planning", l: "Planning to seek assessment" },
        ],
      },
      {
        id: "goal",
        q: "What is your main recovery goal?",
        type: "single",
        opts: [
          { v: "reduce", l: "Reduce activity limitation", boost: { [PHYSIOMOVEFLEX]: 6 } },
          { v: "mobility", l: "Improve mobility", boost: { [PHYSIOMOVEFLEX]: 8 } },
          { v: "strength", l: "Improve strength", boost: { [PHYSIOFUEL]: 6 } },
          { v: "return", l: "Return to normal activities" },
          { v: "rehab", l: "Rehabilitation support", boost: { [PHYSIOMOVEFLEX]: 8 } },
        ],
      },
    ],
    products: [
      { slug: PHYSIOMOVEFLEX, weight: 35, role: "primary" },
      { slug: MAG_FORTE, weight: 15, role: "supporting" },
    ],
  },

  "neck-pain": {
    slug: "neck-pain",
    name: "Neck / Cervical Problems",
    system: "spine",
    tag: "Neck Recovery",
    tagline: "Nutrition alongside cervical rehabilitation.",
    result_headline: "Your Neck Rehabilitation Pathway",
    result_focus: "Cervical Mobility Support",
    result_category: "Joint & Mobility",
    protocol_link: "/conditions/back-neck-rehabilitation",
    protocol_label: "Explore Neck Rehabilitation Protocol",
    questions: [
      {
        id: "trigger",
        q: "What triggers your neck symptoms most?",
        type: "single",
        opts: [
          { v: "desk", l: "Prolonged desk / screen work" },
          { v: "movement", l: "Certain neck movements" },
          { v: "sleep", l: "On waking" },
          { v: "constant", l: "Constant" },
          RED_FLAGS.neuro_symptoms,
        ],
      },
      {
        id: "referred",
        q: "Do symptoms refer to arm or hand?",
        type: "single",
        opts: [
          { v: "no", l: "No — neck only" },
          { v: "shoulder", l: "To shoulder" },
          { v: "arm", l: "Into arm" },
          { v: "hand", l: "Into hand / fingers", redFlag: true },
        ],
      },
      {
        id: "physio",
        q: "Are you currently doing physiotherapy?",
        type: "single",
        opts: [
          { v: "yes", l: "Yes", boost: { [PHYSIOMOVEFLEX]: 8 } },
          { v: "no", l: "No" },
          { v: "planning", l: "Planning to start" },
        ],
      },
      {
        id: "goal",
        q: "What is your main goal?",
        type: "single",
        opts: [
          { v: "mobility", l: "Better neck mobility", boost: { [PHYSIOMOVEFLEX]: 10 } },
          { v: "posture", l: "Support posture", boost: { [PHYSIOMOVEFLEX]: 6, [MAG_FORTE]: 4 } },
          { v: "recovery", l: "Everyday recovery" },
        ],
      },
    ],
    products: [
      { slug: PHYSIOMOVEFLEX, weight: 40, role: "primary" },
      { slug: MAG_FORTE, weight: 18, role: "supporting" },
    ],
  },

  // ─── NERVE PATHWAYS ────────────────────────────────────────────────────────

  "neuropathy": {
    slug: "neuropathy",
    name: "Neuropathy / Nerve Symptoms",
    system: "nerve",
    tag: "Nerve Health",
    tagline: "B12 nutritional support in the appropriate context.",
    result_headline: "Your Nerve Nutritional Support Pathway",
    result_focus: "B12 Nutritional Support",
    result_category: "Nerve Health",
    protocol_link: "/conditions/b12-support",
    protocol_label: "Learn About Nerve-Health Nutrition",
    disclaimer: "This tool does not diagnose neuropathy. B12 Spray is a nutritional supplement, not a treatment for nerve disease.",
    questions: [
      {
        id: "safety_gate",
        q: "Do you have any of the following? (choose all that apply)",
        type: "multi",
        opts: [
          RED_FLAGS.progressive_weakness,
          RED_FLAGS.bladder_bowel,
          { v: "sudden_onset", l: "Sudden severe onset of symptoms", redFlag: true },
          { v: "none", l: "None of these" },
        ],
      },
      {
        id: "sensation",
        q: "What type of sensation are you experiencing?",
        type: "single",
        opts: [
          { v: "tingling", l: "Tingling" },
          { v: "numbness", l: "Numbness" },
          { v: "burning", l: "Burning" },
          { v: "electric", l: "Electric / shooting" },
          { v: "none", l: "None currently" },
        ],
      },
      {
        id: "location",
        q: "Where do you experience it?",
        type: "single",
        opts: [
          { v: "feet", l: "Feet" },
          { v: "legs", l: "Legs" },
          { v: "hands", l: "Hands" },
          { v: "arms", l: "Arms" },
          { v: "multiple", l: "Multiple areas" },
        ],
      },
      {
        id: "sides",
        q: "One side or both?",
        type: "single",
        opts: [
          { v: "one", l: "One side" },
          { v: "both", l: "Both sides" },
          { v: "multiple", l: "Multiple areas" },
          { v: "unsure", l: "Not sure" },
        ],
      },
      {
        id: "evaluated",
        q: "Has a healthcare professional evaluated these symptoms?",
        type: "single",
        opts: [
          { v: "yes", l: "Yes" },
          { v: "no", l: "No" },
          { v: "unsure", l: "Not sure" },
        ],
      },
      {
        id: "b12_test",
        q: "Have you had B12 testing?",
        type: "single",
        opts: [
          { v: "yes_low", l: "Yes — low result", boost: { [B12]: 15 } },
          { v: "yes_normal", l: "Yes — normal result" },
          { v: "no", l: "No" },
          { v: "unsure", l: "Not sure" },
        ],
      },
      {
        id: "recommended",
        q: "Has B12 supplementation been recommended?",
        type: "single",
        opts: [
          { v: "yes", l: "Yes", boost: { [B12]: 14 } },
          { v: "no", l: "No" },
          { v: "unsure", l: "Not sure" },
        ],
      },
      {
        id: "looking",
        q: "What are you looking for?",
        type: "single",
        opts: [
          { v: "b12", l: "B12 nutritional support", boost: { [B12]: 12 } },
          { v: "info", l: "General nerve-health information" },
          { v: "rehab", l: "Rehabilitation support" },
          { v: "prep", l: "Understanding what to discuss with my clinician" },
        ],
      },
    ],
    products: [
      { slug: B12, weight: 30, role: "primary" },
    ],
  },

  // ─── VASCULAR / VENOUS PATHWAYS ────────────────────────────────────────────

  "spider-veins": {
    slug: "spider-veins",
    name: "Spider Veins",
    system: "vascular",
    tag: "Vein Wellness",
    tagline: "Everyday vein-health nutritional support.",
    result_headline: "Your Vein Wellness Pathway",
    result_focus: "Everyday Vein Nutrition",
    result_category: "Vascular Wellness",
    protocol_link: "/for-physiotherapists",
    protocol_label: "Learn About Vein Wellness",
    disclaimer: "Physio Essential does not claim to remove spider veins. If veins are new, symptomatic or changing, a healthcare professional should assess them.",
    questions: [
      {
        id: "location",
        q: "Where are the visible veins?",
        type: "single",
        opts: [
          { v: "thighs", l: "Thighs" },
          { v: "calves", l: "Calves" },
          { v: "knees", l: "Around knees" },
          { v: "multiple", l: "Multiple areas" },
        ],
      },
      {
        id: "symptoms",
        q: "Do you have symptoms in the same area?",
        type: "single",
        opts: [
          { v: "none", l: "No symptoms" },
          { v: "heaviness", l: "Heaviness", boost: { [VARIFLUX]: 8 } },
          { v: "aching", l: "Aching", boost: { [VARIFLUX]: 6 } },
          { v: "itching", l: "Itching" },
          { v: "swelling", l: "Swelling", boost: { [VARIFLUX]: 4 } },
          { v: "other", l: "Other" },
        ],
      },
      {
        id: "standing",
        q: "Are symptoms worse after prolonged standing?",
        type: "single",
        opts: [
          { v: "yes", l: "Yes", boost: { [VARIFLUX]: 6 } },
          { v: "no", l: "No" },
          { v: "unsure", l: "Not sure" },
        ],
      },
      {
        id: "elevation",
        q: "Do symptoms improve when legs are elevated?",
        type: "single",
        opts: [
          { v: "yes", l: "Yes" },
          { v: "no", l: "No" },
          { v: "unsure", l: "Not sure" },
        ],
      },
      {
        id: "goal",
        q: "What are you looking for?",
        type: "single",
        opts: [
          { v: "info", l: "General vein-health information" },
          { v: "support", l: "Lifestyle / recovery support", boost: { [VARIFLUX]: 8 } },
          { v: "eval", l: "Guidance on professional evaluation" },
        ],
      },
    ],
    products: [
      { slug: VARIFLUX, weight: 35, role: "primary" },
    ],
  },

  "varicose-veins": {
    slug: "varicose-veins",
    name: "Varicose Veins",
    system: "vascular",
    tag: "Vein Wellness",
    tagline: "Vein-health nutrition alongside professional care.",
    result_headline: "Your Vein Wellness Pathway",
    result_focus: "Vein-Health Nutrition",
    result_category: "Vascular Wellness",
    protocol_link: "/for-physiotherapists",
    protocol_label: "Learn About Vein Wellness",
    disclaimer: "VariFlux is a nutritional supplement — not a treatment for varicose veins or venous disease. Follow your healthcare professional's advice on compression and venous management.",
    questions: [
      {
        id: "safety_gate",
        q: "Any of these apply? (choose all that apply)",
        type: "multi",
        opts: [
          RED_FLAGS.sudden_swelling,
          { v: "hot_red_calf", l: "Hot, tender, red calf", redFlag: true },
          RED_FLAGS.worsening_wound,
          { v: "none", l: "None of these" },
        ],
      },
      {
        id: "symptoms",
        q: "What are you experiencing? (choose all that apply)",
        type: "multi",
        opts: [
          { v: "visible", l: "Visible enlarged veins" },
          { v: "heaviness", l: "Leg heaviness", boost: { [VARIFLUX]: 10 } },
          { v: "aching", l: "Aching", boost: { [VARIFLUX]: 8 } },
          { v: "swelling", l: "Swelling", boost: { [VARIFLUX]: 6 } },
          { v: "itching", l: "Itching" },
          { v: "skin", l: "Skin changes" },
        ],
      },
      {
        id: "worse_when",
        q: "When are symptoms worse?",
        type: "single",
        opts: [
          { v: "standing", l: "After prolonged standing", boost: { [VARIFLUX]: 6 } },
          { v: "evening", l: "At the end of the day", boost: { [VARIFLUX]: 6 } },
          { v: "travel", l: "During long travel" },
          { v: "exercise", l: "During exercise" },
          { v: "none", l: "No clear pattern" },
        ],
      },
      {
        id: "relief",
        q: "Do symptoms improve with:",
        type: "single",
        opts: [
          { v: "elevation", l: "Leg elevation" },
          { v: "walking", l: "Movement / walking" },
          { v: "rest", l: "Rest" },
          { v: "nothing", l: "Nothing helps" },
          { v: "unsure", l: "Not sure" },
        ],
      },
      {
        id: "evaluated",
        q: "Have you previously been evaluated for venous disease?",
        type: "single",
        opts: [
          { v: "yes", l: "Yes" },
          { v: "no", l: "No" },
          { v: "unsure", l: "Not sure" },
        ],
      },
      {
        id: "compression",
        q: "Have you been advised to use compression therapy?",
        type: "single",
        opts: [
          { v: "yes", l: "Yes" },
          { v: "no", l: "No" },
          { v: "unsure", l: "Not sure" },
        ],
      },
    ],
    products: [
      { slug: VARIFLUX, weight: 40, role: "primary" },
    ],
  },

  "chronic-venous-insufficiency": {
    slug: "chronic-venous-insufficiency",
    name: "Chronic Venous Symptoms",
    system: "vascular",
    tag: "Vein Wellness",
    tagline: "Vein-health nutrition alongside vascular care.",
    result_headline: "Your Vein Wellness Pathway",
    result_focus: "Vein-Health Nutrition",
    result_category: "Vascular Wellness",
    protocol_link: "/for-physiotherapists",
    protocol_label: "Learn About Vein Wellness",
    disclaimer: "This tool does not diagnose chronic venous insufficiency and VariFlux is not a treatment. Follow your treating clinician's advice.",
    questions: [
      {
        id: "safety_gate",
        q: "Any of these apply? (choose all that apply)",
        type: "multi",
        opts: [
          RED_FLAGS.sudden_swelling,
          { v: "hot_red_calf", l: "Hot, tender, red calf", redFlag: true },
          RED_FLAGS.worsening_wound,
          { v: "none", l: "None of these" },
        ],
      },
      {
        id: "swelling",
        q: "Do you experience leg swelling?",
        type: "single",
        opts: [
          { v: "no", l: "No" },
          { v: "occasional", l: "Occasionally", boost: { [VARIFLUX]: 4 } },
          { v: "frequent", l: "Frequently", boost: { [VARIFLUX]: 8 } },
          { v: "daily", l: "Daily", boost: { [VARIFLUX]: 10 } },
        ],
      },
      {
        id: "when",
        q: "When does swelling / heaviness usually occur?",
        type: "single",
        opts: [
          { v: "evening", l: "End of day", boost: { [VARIFLUX]: 6 } },
          { v: "standing", l: "After standing", boost: { [VARIFLUX]: 6 } },
          { v: "sitting", l: "After sitting long" },
          { v: "travel", l: "During travel" },
          { v: "throughout", l: "Throughout the day" },
        ],
      },
      {
        id: "symptoms",
        q: "Do you experience any of these? (choose all that apply)",
        type: "multi",
        opts: [
          { v: "heaviness", l: "Leg heaviness", boost: { [VARIFLUX]: 6 } },
          { v: "aching", l: "Aching", boost: { [VARIFLUX]: 4 } },
          { v: "itching", l: "Itching" },
          { v: "visible", l: "Visible veins" },
          { v: "discoloration", l: "Skin discoloration" },
          { v: "none", l: "None" },
        ],
      },
      {
        id: "elevation",
        q: "Does leg elevation help?",
        type: "single",
        opts: [
          { v: "yes", l: "Yes", boost: { [VARIFLUX]: 4 } },
          { v: "no", l: "No" },
          { v: "unsure", l: "Not sure" },
        ],
      },
      {
        id: "doppler",
        q: "Have you had a venous Doppler / vascular assessment?",
        type: "single",
        opts: [
          { v: "yes", l: "Yes" },
          { v: "no", l: "No" },
          { v: "unsure", l: "Not sure" },
        ],
      },
      {
        id: "care",
        q: "Are you currently under vascular / medical care?",
        type: "single",
        opts: [
          { v: "yes", l: "Yes" },
          { v: "no", l: "No" },
        ],
      },
    ],
    products: [
      { slug: VARIFLUX, weight: 40, role: "primary" },
    ],
  },

  "leg-heaviness": {
    slug: "leg-heaviness",
    name: "Leg Heaviness",
    system: "vascular",
    tag: "Vein Wellness",
    tagline: "General vein-wellness nutrition.",
    result_headline: "Your Vein Wellness Pathway",
    result_focus: "Everyday Vein Nutrition",
    result_category: "Vascular Wellness",
    protocol_link: "/for-physiotherapists",
    protocol_label: "Learn About Vein Wellness",
    questions: [
      {
        id: "safety_gate",
        q: "Any of these apply? (choose all that apply)",
        type: "multi",
        opts: [
          RED_FLAGS.sudden_swelling,
          { v: "hot_red_calf", l: "Hot, tender, red calf", redFlag: true },
          { v: "none", l: "None of these" },
        ],
      },
      {
        id: "when",
        q: "When is heaviness worst?",
        type: "single",
        opts: [
          { v: "evening", l: "End of day", boost: { [VARIFLUX]: 6 } },
          { v: "standing", l: "After standing", boost: { [VARIFLUX]: 6 } },
          { v: "travel", l: "During travel" },
          { v: "morning", l: "In the morning" },
        ],
      },
      {
        id: "elevation",
        q: "Does elevating your legs help?",
        type: "single",
        opts: [
          { v: "yes", l: "Yes", boost: { [VARIFLUX]: 6 } },
          { v: "no", l: "No" },
          { v: "unsure", l: "Not sure" },
        ],
      },
      {
        id: "goal",
        q: "What are you looking for?",
        type: "single",
        opts: [
          { v: "wellness", l: "General vein-wellness nutrition", boost: { [VARIFLUX]: 8 } },
          { v: "lifestyle", l: "Lifestyle guidance" },
          { v: "eval", l: "Guidance on professional evaluation" },
        ],
      },
    ],
    products: [
      { slug: VARIFLUX, weight: 35, role: "primary" },
    ],
  },

  "leg-itching": {
    slug: "leg-itching",
    name: "Leg / Calf Itching",
    system: "vascular",
    tag: "Vein Wellness",
    tagline: "Only nutritional support — no assumed cause.",
    result_headline: "Your Next Step",
    result_focus: "Professional Evaluation Encouraged",
    result_category: "Vascular Wellness",
    protocol_link: "/for-physiotherapists",
    protocol_label: "Learn About Vein Wellness",
    disclaimer: "Leg itching has many possible causes. This tool does not identify the cause. If itching is accompanied by skin changes, wounds or swelling, a healthcare professional should assess it.",
    // Very cautious — no product forced. Only shown as optional nutritional support after safety gate.
    questions: [
      {
        id: "safety_gate",
        q: "Any of these present? (choose all that apply)",
        type: "multi",
        opts: [
          RED_FLAGS.worsening_wound,
          { v: "skin_ulcer", l: "Open sore or ulcer on the leg", redFlag: true },
          { v: "significant_swelling", l: "Significant new swelling", redFlag: true },
          { v: "none", l: "None of these" },
        ],
      },
      {
        id: "location",
        q: "Where is the itching?",
        type: "single",
        opts: [
          { v: "calf", l: "Calf" },
          { v: "ankle", l: "Ankle" },
          { v: "lower_leg", l: "Lower leg" },
          { v: "multiple", l: "Multiple areas" },
        ],
      },
      {
        id: "also",
        q: "Do you also have any of these? (choose all that apply)",
        type: "multi",
        opts: [
          { v: "swelling", l: "Swelling", boost: { [VARIFLUX]: 3 } },
          { v: "visible", l: "Visible veins", boost: { [VARIFLUX]: 3 } },
          { v: "discoloration", l: "Skin discoloration" },
          { v: "dry", l: "Dry skin" },
          { v: "rash", l: "Rash" },
          { v: "heaviness", l: "Leg heaviness", boost: { [VARIFLUX]: 3 } },
          { v: "none", l: "None" },
        ],
      },
      {
        id: "evaluated",
        q: "Has a healthcare professional evaluated it?",
        type: "single",
        opts: [
          { v: "yes", l: "Yes" },
          { v: "no", l: "No" },
        ],
      },
    ],
    products: [
      { slug: VARIFLUX, weight: 20, role: "supporting" },
    ],
  },

  "calf-pain-walking": {
    slug: "calf-pain-walking",
    name: "Calf Pain While Walking",
    system: "vascular",
    tag: "Vascular Assessment",
    tagline: "This pattern needs a professional look, not a supplement.",
    result_headline: "Your Next Step",
    result_focus: "Vascular / Medical Assessment",
    result_category: "Vascular Assessment",
    protocol_link: "/for-physiotherapists",
    protocol_label: "Find a Qualified Professional",
    disclaimer: "Walking-related calf pain that improves with rest can be related to arterial circulation and requires medical assessment. Do NOT self-treat with supplements.",
    // no_product_pathway — the engine will force the "consult professional" screen
    no_product_pathway: true,
    questions: [
      {
        id: "predictable",
        q: "Does calf pain appear predictably after walking a certain distance?",
        type: "single",
        opts: [
          { v: "yes", l: "Yes", redFlag: true },
          { v: "no", l: "No" },
          { v: "unsure", l: "Not sure" },
        ],
      },
      {
        id: "relief",
        q: "Does the pain improve after stopping / resting?",
        type: "single",
        opts: [
          { v: "yes", l: "Yes", redFlag: true },
          { v: "no", l: "No" },
          { v: "unsure", l: "Not sure" },
        ],
      },
      {
        id: "side",
        q: "Which leg?",
        type: "single",
        opts: [
          { v: "right", l: "Right" },
          { v: "left", l: "Left" },
          { v: "both", l: "Both" },
          { v: "unsure", l: "Not sure" },
        ],
      },
      {
        id: "risk_factors",
        q: "Do you have any of these? (choose all that apply)",
        type: "multi",
        opts: [
          { v: "smoking", l: "Smoking history", redFlag: true },
          { v: "diabetes", l: "Diabetes", redFlag: true },
          { v: "bp", l: "High blood pressure" },
          { v: "cholesterol", l: "High cholesterol" },
          { v: "vascular", l: "Known vascular disease", redFlag: true },
          { v: "none", l: "None of these" },
        ],
      },
      {
        id: "foot_signs",
        q: "Do you have any of these? (choose all that apply)",
        type: "multi",
        opts: [
          RED_FLAGS.cold_pale_foot,
          { v: "foot_wound", l: "Foot wound", redFlag: true },
          { v: "foot_ulcer", l: "Foot ulcer", redFlag: true },
          { v: "color_change", l: "Color change of foot / toes", redFlag: true },
          { v: "numb_foot", l: "Numb foot", redFlag: true },
          { v: "none", l: "None" },
        ],
      },
    ],
    products: [],
  },

  "smoking-vascular": {
    slug: "smoking-vascular",
    name: "Smoking & Vascular Health",
    system: "vascular",
    tag: "Vascular Health",
    tagline: "Vascular symptoms in smokers need professional assessment.",
    result_headline: "Your Next Step",
    result_focus: "Vascular Assessment",
    result_category: "Vascular Assessment",
    protocol_link: "/for-physiotherapists",
    protocol_label: "Learn About Vascular Health",
    disclaimer: "Symptoms involving circulation, walking-related calf pain or foot wounds require professional assessment — a supplement is not the answer here.",
    questions: [
      {
        id: "exposure",
        q: "What is your current nicotine exposure?",
        type: "single",
        opts: [
          { v: "cigarettes", l: "Cigarettes" },
          { v: "vaping", l: "Vaping / e-cigarettes" },
          { v: "other", l: "Other nicotine products" },
          { v: "former", l: "Former smoker" },
          { v: "none", l: "No current nicotine use" },
        ],
      },
      {
        id: "duration",
        q: "How long have you used nicotine?",
        type: "single",
        opts: [
          { v: "u1", l: "Less than 1 year" },
          { v: "1_5", l: "1–5 years" },
          { v: "5_10", l: "5–10 years" },
          { v: "10_plus", l: "More than 10 years" },
          { v: "former", l: "Former user" },
        ],
      },
      {
        id: "symptoms",
        q: "Are you experiencing any of these? (choose all that apply)",
        type: "multi",
        opts: [
          { v: "calf_walking", l: "Calf pain during walking", redFlag: true },
          { v: "cold_feet", l: "Cold feet", redFlag: true },
          { v: "numbness", l: "Numbness", redFlag: true },
          { v: "slow_healing", l: "Slow-healing wounds", redFlag: true },
          { v: "ulcer", l: "Foot ulcer", redFlag: true },
          { v: "color_change", l: "Color changes in toes / feet", redFlag: true },
          { v: "none", l: "None" },
        ],
      },
      {
        id: "assessed",
        q: "Has a healthcare professional assessed your circulation?",
        type: "single",
        opts: [
          { v: "yes", l: "Yes" },
          { v: "no", l: "No" },
          { v: "unsure", l: "Not sure" },
        ],
      },
    ],
    products: [],
  },

  "foot-ulcer": {
    slug: "foot-ulcer",
    name: "Foot Ulcer / Non-Healing Wound",
    system: "vascular",
    tag: "Wound Care",
    tagline: "This is a clinical situation — please seek assessment.",
    result_headline: "Your Next Step",
    result_focus: "Prompt Medical Assessment",
    result_category: "Wound & Vascular Care",
    protocol_link: "/for-physiotherapists",
    protocol_label: "Find a Qualified Professional",
    disclaimer: "Foot ulcers and non-healing wounds are clinical situations that need appropriate assessment (wound care, vascular evaluation, diabetes review where relevant). Physio Essential does not offer treatment for ulcers.",
    no_product_pathway: true,
    questions: [
      {
        id: "open",
        q: "Do you currently have an open wound or ulcer?",
        type: "single",
        opts: [
          { v: "yes", l: "Yes", redFlag: true },
          { v: "no", l: "No" },
          { v: "unsure", l: "Not sure", redFlag: true },
        ],
      },
      {
        id: "location",
        q: "Where is it?",
        type: "single",
        opts: [
          { v: "toe", l: "Toe" },
          { v: "sole", l: "Sole" },
          { v: "heel", l: "Heel" },
          { v: "side", l: "Side of foot" },
          { v: "other", l: "Other" },
        ],
      },
      {
        id: "duration",
        q: "How long has it been present?",
        type: "single",
        opts: [
          { v: "u1w", l: "Less than 1 week", redFlag: true },
          { v: "1_4w", l: "1–4 weeks", redFlag: true },
          { v: "over_4w", l: "More than 4 weeks", redFlag: true },
          { v: "unsure", l: "Not sure", redFlag: true },
        ],
      },
      {
        id: "signs",
        q: "Any of these signs? (choose all that apply)",
        type: "multi",
        opts: [
          { v: "redness", l: "Increasing redness", redFlag: true },
          { v: "swelling", l: "Increasing swelling", redFlag: true },
          { v: "discharge", l: "Discharge", redFlag: true },
          { v: "smell", l: "Bad smell", redFlag: true },
          RED_FLAGS.fever_wound,
          { v: "pain", l: "Increasing pain", redFlag: true },
          RED_FLAGS.black_tissue,
          { v: "none", l: "None of these" },
        ],
      },
      {
        id: "assessed",
        q: "Has it been assessed by a healthcare professional?",
        type: "single",
        opts: [
          { v: "yes", l: "Yes" },
          { v: "no", l: "No" },
        ],
      },
    ],
    products: [],
  },

  // ─── AVN PATHWAYS ─────────────────────────────────────────────────────────

  "hip-avn": {
    slug: "hip-avn",
    name: "Hip Avascular Necrosis",
    system: "avn",
    tag: "AVN — Hip",
    tagline: "Specialist care first; nutrition is only complementary.",
    result_headline: "Your Next Step",
    result_focus: "Specialist Management + Rehabilitation",
    result_category: "AVN Rehabilitation",
    protocol_link: "/for-physiotherapists",
    protocol_label: "Learn About AVN Rehabilitation",
    disclaimer: "Physio Essential products do not treat, reverse or prevent progression of avascular necrosis. Specialist management is essential.",
    questions: [
      {
        id: "diagnosed",
        q: "Has AVN been diagnosed by a healthcare professional?",
        type: "single",
        opts: [
          { v: "yes", l: "Yes" },
          { v: "suspected", l: "Suspected only", redFlag: true },
          { v: "no", l: "No" },
        ],
      },
      {
        id: "stage",
        q: "What stage of management are you in?",
        type: "single",
        opts: [
          { v: "recent", l: "Recently diagnosed" },
          { v: "active", l: "Under active medical treatment" },
          { v: "rehab", l: "Physiotherapy / rehabilitation", boost: { [PHYSIOMOVEFLEX]: 8 } },
          { v: "post_op", l: "Post-surgical rehabilitation", boost: { [PHYSIOMOVEFLEX]: 8 } },
          { v: "monitoring", l: "Monitoring" },
          { v: "not_yet", l: "Not yet evaluated", redFlag: true },
        ],
      },
      {
        id: "function",
        q: "What is your main functional limitation?",
        type: "single",
        opts: [
          { v: "pain", l: "Pain" },
          { v: "mobility", l: "Reduced mobility", boost: { [PHYSIOMOVEFLEX]: 6 } },
          { v: "walking", l: "Difficulty walking" },
          { v: "using", l: "Difficulty using the joint" },
          { v: "exercise", l: "Reduced exercise capacity" },
        ],
      },
      {
        id: "goal",
        q: "What is your main recovery goal?",
        type: "single",
        opts: [
          { v: "mobility", l: "Maintain mobility", boost: { [PHYSIOMOVEFLEX]: 8 } },
          { v: "rehab", l: "Support rehabilitation", boost: { [PHYSIOMOVEFLEX]: 6 } },
          { v: "strength", l: "Improve strength", boost: { [PHYSIOFUEL]: 4 } },
          { v: "function", l: "Maintain function" },
          { v: "post_op", l: "Post-operative recovery", boost: { [MAG_FORTE]: 4 } },
        ],
      },
    ],
    products: [
      { slug: PHYSIOMOVEFLEX, weight: 20, role: "supporting" },
      { slug: MAG_FORTE, weight: 12, role: "supporting" },
    ],
  },

  "knee-avn": {
    slug: "knee-avn",
    name: "Knee Avascular Necrosis",
    system: "avn",
    tag: "AVN — Knee",
    tagline: "Specialist care first; nutrition is only complementary.",
    result_headline: "Your Next Step",
    result_focus: "Specialist Management + Rehabilitation",
    result_category: "AVN Rehabilitation",
    protocol_link: "/for-physiotherapists",
    protocol_label: "Learn About AVN Rehabilitation",
    disclaimer: "Physio Essential products do not treat, reverse or prevent progression of avascular necrosis. Specialist management is essential.",
    questions: [
      {
        id: "diagnosed",
        q: "Has AVN been diagnosed by a healthcare professional?",
        type: "single",
        opts: [
          { v: "yes", l: "Yes" },
          { v: "suspected", l: "Suspected only", redFlag: true },
          { v: "no", l: "No" },
        ],
      },
      {
        id: "stage",
        q: "What stage of management are you in?",
        type: "single",
        opts: [
          { v: "recent", l: "Recently diagnosed" },
          { v: "active", l: "Under active medical treatment" },
          { v: "rehab", l: "Physiotherapy / rehabilitation", boost: { [PHYSIOMOVEFLEX]: 8 } },
          { v: "post_op", l: "Post-surgical rehabilitation", boost: { [PHYSIOMOVEFLEX]: 8 } },
          { v: "monitoring", l: "Monitoring" },
          { v: "not_yet", l: "Not yet evaluated", redFlag: true },
        ],
      },
      {
        id: "function",
        q: "What is your main functional limitation?",
        type: "single",
        opts: [
          { v: "pain", l: "Pain" },
          { v: "mobility", l: "Reduced mobility", boost: { [PHYSIOMOVEFLEX]: 6 } },
          { v: "walking", l: "Difficulty walking" },
          { v: "exercise", l: "Reduced exercise capacity" },
        ],
      },
      {
        id: "goal",
        q: "What is your main recovery goal?",
        type: "single",
        opts: [
          { v: "mobility", l: "Maintain mobility", boost: { [PHYSIOMOVEFLEX]: 8 } },
          { v: "rehab", l: "Support rehabilitation", boost: { [PHYSIOMOVEFLEX]: 6 } },
          { v: "strength", l: "Improve strength", boost: { [PHYSIOFUEL]: 4 } },
          { v: "function", l: "Maintain function" },
        ],
      },
    ],
    products: [
      { slug: PHYSIOMOVEFLEX, weight: 20, role: "supporting" },
      { slug: MAG_FORTE, weight: 12, role: "supporting" },
    ],
  },

  "shoulder-avn": {
    slug: "shoulder-avn",
    name: "Shoulder Avascular Necrosis",
    system: "avn",
    tag: "AVN — Shoulder",
    tagline: "Specialist care first; nutrition is only complementary.",
    result_headline: "Your Next Step",
    result_focus: "Specialist Management + Rehabilitation",
    result_category: "AVN Rehabilitation",
    protocol_link: "/for-physiotherapists",
    protocol_label: "Learn About AVN Rehabilitation",
    disclaimer: "Physio Essential products do not treat, reverse or prevent progression of avascular necrosis. Specialist management is essential.",
    questions: [
      {
        id: "diagnosed",
        q: "Has AVN been diagnosed by a healthcare professional?",
        type: "single",
        opts: [
          { v: "yes", l: "Yes" },
          { v: "suspected", l: "Suspected only", redFlag: true },
          { v: "no", l: "No" },
        ],
      },
      {
        id: "stage",
        q: "What stage of management are you in?",
        type: "single",
        opts: [
          { v: "recent", l: "Recently diagnosed" },
          { v: "active", l: "Under active medical treatment" },
          { v: "rehab", l: "Physiotherapy / rehabilitation", boost: { [PHYSIOMOVEFLEX]: 8 } },
          { v: "post_op", l: "Post-surgical rehabilitation", boost: { [PHYSIOMOVEFLEX]: 8 } },
          { v: "monitoring", l: "Monitoring" },
          { v: "not_yet", l: "Not yet evaluated", redFlag: true },
        ],
      },
      {
        id: "function",
        q: "What is your main functional limitation?",
        type: "single",
        opts: [
          { v: "pain", l: "Pain" },
          { v: "mobility", l: "Reduced mobility", boost: { [PHYSIOMOVEFLEX]: 6 } },
          { v: "using", l: "Difficulty using the arm" },
          { v: "exercise", l: "Reduced exercise capacity" },
        ],
      },
      {
        id: "goal",
        q: "What is your main recovery goal?",
        type: "single",
        opts: [
          { v: "mobility", l: "Maintain mobility", boost: { [PHYSIOMOVEFLEX]: 8 } },
          { v: "rehab", l: "Support rehabilitation", boost: { [PHYSIOMOVEFLEX]: 6 } },
          { v: "strength", l: "Improve strength", boost: { [PHYSIOFUEL]: 4 } },
          { v: "function", l: "Maintain function" },
        ],
      },
    ],
    products: [
      { slug: PHYSIOMOVEFLEX, weight: 20, role: "supporting" },
      { slug: MAG_FORTE, weight: 12, role: "supporting" },
    ],
  },
};

/**
 * Compute product scores from answers.
 * Returns [{slug, score, role}] sorted DESC.
 * Detects red flags (any redFlag option selected) and returns { redFlag: true }.
 */
export function scoreQuiz(conditionSlug, answers) {
  const cfg = QUIZ_CONDITIONS[conditionSlug];
  if (!cfg) return { scores: [], redFlag: false };

  // Detect red flag: any option marked redFlag was chosen
  let redFlag = false;
  for (const q of cfg.questions) {
    const ans = answers[q.id];
    if (!ans) continue;
    const vals = Array.isArray(ans) ? ans : [ans];
    for (const v of vals) {
      const opt = q.opts.find((o) => o.v === v);
      if (opt && opt.redFlag) redFlag = true;
    }
  }
  if (redFlag) return { scores: [], redFlag: true };

  // Base weights
  const scores = {};
  const roles = {};
  for (const p of cfg.products) {
    scores[p.slug] = p.weight;
    roles[p.slug] = p.role;
  }

  // Apply per-answer boosts
  for (const q of cfg.questions) {
    const ans = answers[q.id];
    if (!ans) continue;
    const vals = Array.isArray(ans) ? ans : [ans];
    for (const v of vals) {
      const opt = q.opts.find((o) => o.v === v);
      if (!opt || !opt.boost) continue;
      for (const [slug, delta] of Object.entries(opt.boost)) {
        scores[slug] = (scores[slug] || 0) + delta;
      }
    }
  }

  const list = Object.entries(scores)
    .map(([slug, score]) => ({ slug, score, role: roles[slug] || "supporting" }))
    .sort((a, b) => b.score - a.score);

  return { scores: list, redFlag: false };
}
